import { camelCase } from 'lodash';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { modelOf } from '../../../../prop-types';
import ConfigStore from '../../../../store/ConfigStore';
import UIStore from '../../../../store/UIStore';

const MOBILE_SQUARE_SIZE = 577;

export const countMainAxisSize = (crossAxisSize, localAspectRatio) =>
  crossAxisSize / localAspectRatio;

export const countMinCrossAxisSize = (localMaximumCrossAxisSize, axisSize) =>
  Math.min(localMaximumCrossAxisSize, axisSize);

const BannerSliderStyles = ({
  configStore,
  uiStore,
  bannerSliderId,
  bannerSlider,
  elementWidth,
  aspectRatio,
  maximumCrossAxisSize,
  ifVertical,
  slidesToShow,
  variableWidth,
  mobileSquareMode,
  trackMinWidth,
}) => {
  const getBannerWrapperSelector = () =>
    uiStore.isSmallest ? '.Banner__wrapper-mobile' : '.Banner__wrapper';

  const shouldRemoveScentHeight = () => {
    return (
      !!configStore.banner.scent.pattern &&
      !(uiStore.isMobile && !configStore.banner.scent.shouldShowMobile) &&
      configStore.banner.scent.onTop
    );
  };

  const getScentHeight = () => {
    return configStore.getScentHeight(uiStore.isMobile);
  };

  const getContentScentHeight = () => {
    return shouldRemoveScentHeight() ? configStore.banner.scent.height : 0;
  };

  const getMainAxis = () => (ifVertical ? 'height' : 'width');

  const getCrossAxis = () => (ifVertical ? 'width' : 'height');

  const getCrossAxisSize = () =>
    bannerSlider && bannerSlider[camelCase('client ' + getCrossAxis())];

  const getCSS = useMemo(() => {
    const localAspectRatio = aspectRatio;
    const localMaximumCrossAxisSize = maximumCrossAxisSize;
    const scentHeight = getScentHeight();
    const contentScentHeight = getContentScentHeight();
    const mainAxis = getMainAxis();
    const crossAxis = getCrossAxis();
    const bannerWrapper = getBannerWrapperSelector();
    const bannerBackgroundImage =
      '.Banner__background-image, .Banner__background-mobile-image';

    if (mainAxis === 'width') {
      const mainAxisSize =
        elementWidth > 0
          ? countMainAxisSize(elementWidth, localAspectRatio)
          : localMaximumCrossAxisSize;
      const crossAxisSize = countMinCrossAxisSize(
        localMaximumCrossAxisSize,
        mainAxisSize
      );
      const mobileSquareCrossAxisSize = mobileSquareMode
        ? elementWidth > 0
          ? Math.min(MOBILE_SQUARE_SIZE, elementWidth)
          : MOBILE_SQUARE_SIZE
        : crossAxisSize;
      const maximumSlideCrossAxisSize = Math.max(
        crossAxisSize,
        mobileSquareCrossAxisSize
      );
      const overlayCrossAxisSize = Math.max(
        0,
        crossAxisSize - contentScentHeight
      );
      const mobileSquareOverlayCrossAxisSize = Math.max(
        0,
        mobileSquareCrossAxisSize - contentScentHeight
      );

      return `
        #${bannerSliderId} .BannerSlider__single-slide-layout {
          display: flex;
          justify-content: center;
          max-${mainAxis}: ${elementWidth > 0 ? `${elementWidth}px` : '100%'};
          width: 100%;
        }
        #${bannerSliderId} .BannerSlider__single-slide-layout .BannerSlider__slide {
          flex: 0 0 auto;
        }
        #${bannerSliderId} .BannerSlider__slide {
          ${mainAxis}: ${variableWidth ? 'auto' : '100%'};
          max-${mainAxis}: ${
        variableWidth ? 'none' : elementWidth > 0 ? `${elementWidth}px` : '100%'
      };
        }
        #${bannerSliderId} .BannerSlider__slide .Banner {
          ${mainAxis}: 100%;
          ${crossAxis}: ${crossAxisSize}px;
        }
        #${bannerSliderId} .BannerSlider__slide ${bannerWrapper},
        #${bannerSliderId} .BannerSlider__slide .Banner__video-container,
        #${bannerSliderId} .BannerSlider__slide .Banner__overlay,
        #${bannerSliderId} .BannerSlider__slide .Banner .MaybeExternalLink,
        #${bannerSliderId} .BannerSlider__slide .Banner__media,
        #${bannerSliderId} .BannerSlider__slide picture,
        #${bannerSliderId} .BannerSlider__slide ${bannerBackgroundImage},
        #${bannerSliderId} .BannerSlider__slide .Banner--default-width {
          ${mainAxis}: 100%;
        }
        #${bannerSliderId} .BannerSlider__slide ${bannerWrapper},
        #${bannerSliderId} .BannerSlider__slide .Banner__video-container,
        #${bannerSliderId} .BannerSlider__slide .Banner__video-container video,
        #${bannerSliderId} .BannerSlider__slide .Banner__video-container iframe,
        #${bannerSliderId} .BannerSlider__slide ${bannerBackgroundImage} {
          ${crossAxis}: ${crossAxisSize}px;
        }
        #${bannerSliderId} .BannerSlider__slide .Banner__overlay {
          ${crossAxis}: ${overlayCrossAxisSize}px;
        }
        #${bannerSliderId} .BannerSlider__slide--mobile-letterbox .Banner,
        #${bannerSliderId} .BannerSlider__slide--mobile-letterbox ${bannerWrapper},
        #${bannerSliderId} .BannerSlider__slide--mobile-letterbox .Banner__video-container,
        #${bannerSliderId} .BannerSlider__slide--mobile-letterbox .Banner__video-container video,
        #${bannerSliderId} .BannerSlider__slide--mobile-letterbox .Banner__video-container iframe,
        #${bannerSliderId} .BannerSlider__slide--mobile-letterbox ${bannerBackgroundImage},
        #${bannerSliderId} .BannerSlider__slide--mobile-letterbox picture {
          ${crossAxis}: ${mobileSquareCrossAxisSize}px;
        }
        #${bannerSliderId} .BannerSlider__slide--mobile-letterbox .Banner__overlay {
          ${crossAxis}: ${mobileSquareOverlayCrossAxisSize}px;
        }
        #${bannerSliderId} .BannerSlider__slide--mobile-letterbox .Banner__background-image,
        #${bannerSliderId} .BannerSlider__slide--mobile-letterbox .Banner__background-mobile-image,
        #${bannerSliderId} .BannerSlider__slide--mobile-letterbox .Banner__video-container video {
          object-fit: contain;
        }
        #${bannerSliderId} .BannerSlider__slide--portrait-video .Banner,
        #${bannerSliderId} .BannerSlider__slide--portrait-video .Banner__wrapper,
        #${bannerSliderId} .BannerSlider__slide--portrait-video .Banner__wrapper-mobile,
        #${bannerSliderId} .BannerSlider__slide--portrait-video .Banner__video-container {
          background-color: #000;
        }
        #${bannerSliderId} .slick-slider,
        #${bannerSliderId} .slick-list,
        #${bannerSliderId} .slick-slide,
        #${bannerSliderId} .BannerSlider__single-slide-layout,
        #${bannerSliderId} .BannerSlider__slide,
        #${bannerSliderId} .Banner,
        #${bannerSliderId} .slick-track {
          max-${crossAxis}: ${maximumSlideCrossAxisSize + scentHeight}px;
        }
        ${
          trackMinWidth
            ? `#${bannerSliderId} .slick-track {
          min-width: ${trackMinWidth}px;
        }`
            : ''
        }
        `;
    }

    const crossAxisSize = countMinCrossAxisSize(
      localMaximumCrossAxisSize,
      getCrossAxisSize()
    );
    const mainAxisSize = countMainAxisSize(crossAxisSize, localAspectRatio);
    const sliderSizeByMainAxis = mainAxisSize * slidesToShow;

    return `
        #${bannerSliderId} .BannerSlider__slide img {
          max-${crossAxis}: ${crossAxisSize}px;
          max-${mainAxis}: ${mainAxisSize}px;
        }
        #${bannerSliderId} .BannerSlider__slide .Banner__video-container,
        #${bannerSliderId} .BannerSlider__slide .Banner,
        #${bannerSliderId} .BannerSlider__slide ${bannerWrapper} {
          max-${crossAxis}: ${crossAxisSize}px;
          max-${mainAxis}: ${mainAxisSize}px;
          ${crossAxis}: 100%;
          ${mainAxis}: 100%;
        }
        #${bannerSliderId} .BannerSlider__slide--vertical-image-letterbox img.Banner__background-image,
        #${bannerSliderId} .BannerSlider__slide--vertical-image-letterbox img.Banner__background-mobile-image {
          object-fit: contain;
        }
        #${bannerSliderId} .BannerSlider__slide,
        #${bannerSliderId} .slick-slide {
          ${mainAxis}: ${mainAxisSize}px;
        }

        #${bannerSliderId} .slick-slider,
        #${bannerSliderId} .slick-list,
        #${bannerSliderId} .slick-track {
          ${mainAxis}: ${sliderSizeByMainAxis}px !important;
        }
        `;
  }, [
    aspectRatio,
    bannerSlider,
    bannerSliderId,
    configStore,
    elementWidth,
    ifVertical,
    mobileSquareMode,
    maximumCrossAxisSize,
    slidesToShow,
    trackMinWidth,
    uiStore.isMobile,
    uiStore.isSmallest,
    variableWidth,
  ]);

  return <style>{getCSS}</style>;
};

BannerSliderStyles.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  aspectRatio: PropTypes.number.isRequired,
  maximumCrossAxisSize: PropTypes.number.isRequired,
  bannerSliderId: PropTypes.string,
  elementWidth: PropTypes.number,
  ifVertical: PropTypes.bool,
  slidesToShow: PropTypes.number,
  variableWidth: PropTypes.bool,
  mobileSquareMode: PropTypes.bool,
  trackMinWidth: PropTypes.number,
  bannerSlider: PropTypes.object,
};

export default inject('configStore', 'uiStore')(observer(BannerSliderStyles));
